import { useEffect, useState } from "react";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";

import type { SessionBreadcrumbSegment } from "@/components/session-breadcrumb";
import type { AccessTokenGetter } from "@/lib/utils";
import type { Session } from "@/lib/session";
import type { ConversationBootstrap } from "@/api/conversation-bootstrap";
import { isCanonicalConversationId, type ConversationDestination } from "@/lib/conversation-target";
import i18n from "@/lib/i18n";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, readCache, writeCache } from "@/lib/ui-cache";
import { sessionDetailPreview, type CachedSessionDetail } from "@/lib/ui-cache-shapes";
import { fetchSessionDetailCached } from "@/lib/session-detail-cache";
import { useCacheScope } from "@/hooks/use-cache-scope";

const API_BASE = "/api/sessions";

type BreadcrumbSession = Pick<Session, "id" | "title" | "parent_session_id"> & {
  metadata?: Record<string, any> | null;
  session_type?: string | null;
};

export interface UseSessionBreadcrumbReturn {
  /** Segments ordered from the root session to the active session. */
  segments: SessionBreadcrumbSegment[];
  loading: boolean;
  error: string | null;
}

function toSegments(sessions: readonly BreadcrumbSession[]): SessionBreadcrumbSegment[] {
  return sessions.map((session, index) => {
    const child = sessions[index + 1];
    if (!child) return { id: session.id, title: session.title };
    const anchor = child.metadata?.thread_anchor;
    const sourceMessageId = typeof anchor?.source_message_id === "string"
      ? anchor.source_message_id
      : null;
    const isThreadEdge = child.session_type === "thread" || anchor != null;
    const destination: ConversationDestination | undefined = (
      isThreadEdge
      && isCanonicalConversationId(session.id)
      && isCanonicalConversationId(child.id)
      && sourceMessageId
      && isCanonicalConversationId(sourceMessageId)
    ) ? {
      sessionId: session.id,
      target: { kind: "thread", threadId: child.id, messageId: sourceMessageId },
    } : undefined;
    return {
      id: session.id,
      title: session.title,
      ...(destination ? { destination } : {}),
      ...(isThreadEdge && !destination ? { originUnavailable: true } : {}),
    };
  });
}

/**
 * Resolves the active session's ancestor chain one parent at a time.
 *
 * The active session itself is normally already in the session list, so it
 * can be supplied as `currentSession` to avoid an unnecessary request. With
 * an older server a child session may be absent; callers can omit it while
 * their existing detail fetch resolves and then pass it on the next render.
 */
export function useSessionBreadcrumb(
  sessionId: string | null,
  getToken: AccessTokenGetter,
  currentSession?: BreadcrumbSession | null,
  bootstrap?: ConversationBootstrap | null,
  bootstrapPending = false,
): UseSessionBreadcrumbReturn {
  const verifiedScope = useCacheScope();
  const [segments, setSegments] = useState<SessionBreadcrumbSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentSessionId = currentSession?.id;
  const currentSessionTitle = currentSession?.title;
  const currentSessionParentId = currentSession?.parent_session_id;
  const backendReconnectKey = useBackendReconnectKey();

  useEffect(() => {
    const initialSession: BreadcrumbSession | null = currentSessionId === sessionId
      && currentSessionId
      ? {
          id: currentSessionId,
          title: currentSessionTitle ?? "",
          parent_session_id: currentSessionParentId,
          metadata: currentSession?.metadata,
          session_type: currentSession?.session_type,
        }
      : null;

    if (!sessionId) {
      setSegments([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (bootstrap?.session.id === sessionId) {
      setSegments(toSegments([...bootstrap.breadcrumb_ancestors, bootstrap.session]));
      setLoading(false);
      setError(null);
      return;
    }
    if (bootstrapPending) return;

    const controller = new AbortController();
    let cancelled = false;
    const currentToRoot: BreadcrumbSession[] = initialSession ? [initialSession] : [];
    const visitedIds = new Set<string>(initialSession ? [initialSession.id] : []);

    // Keep the current title visible while any ancestor lookup is in flight.
    setSegments(initialSession ? toSegments([initialSession]) : []);
    setError(null);

    const fetchSession = async (id: string): Promise<BreadcrumbSession> => {
      const response = await fetchSessionDetailCached(
        `${API_BASE}/${encodeURIComponent(id)}`,
        { headers: {}, signal: controller.signal },
        getToken,
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) clearCacheOnAuthFailure();
        if ([401, 403, 404].includes(response.status)) void invalidateCache("contextDetails", "session", id);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const session = await response.json() as Session;
      const preview = sessionDetailPreview(session);
      if (preview) void writeCache("contextDetails", "session", id, preview);
      return session;
    };

    const resolve = async () => {
      setLoading(true);
      try {
        // A disk chain is display-only. Every segment is still requested live
        // before it can establish navigation or access.
        if (verifiedScope) {
          const preview: BreadcrumbSession[] = [...currentToRoot];
          const seen = new Set(preview.map((item) => item.id));
          let next = initialSession?.parent_session_id ?? (initialSession ? null : sessionId);
          while (next && preview.length < 8 && !seen.has(next)) {
            const detail = await readCache<CachedSessionDetail>("contextDetails", "session", next);
            if (!detail || cancelled || cacheScope() !== verifiedScope) break;
            preview.push(detail); seen.add(detail.id); next = detail.parent_session_id;
          }
          if (!cancelled && preview.length > currentToRoot.length) setSegments(toSegments(preview.reverse()));
        }
        let nextSessionId = initialSession
          ? initialSession.parent_session_id ?? null
          : sessionId;

        while (nextSessionId && !visitedIds.has(nextSessionId)) {
          const session = await fetchSession(nextSessionId);
          if (cancelled || controller.signal.aborted) return;
          if (!session.id || visitedIds.has(session.id)) break;

          currentToRoot.push(session);
          visitedIds.add(session.id);
          nextSessionId = session.parent_session_id ?? null;
        }

        if (!cancelled) {
          setSegments(toSegments(currentToRoot.reverse()));
        }
      } catch (reason) {
        if (!cancelled && !controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : i18n.t("breadcrumb.loadFailed"));
          // A partial path is still more useful than hiding the current title.
          setSegments(toSegments(currentToRoot.reverse()));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void resolve();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    backendReconnectKey,
    bootstrap,
    bootstrapPending,
    currentSessionId,
    currentSessionParentId,
    currentSession?.metadata,
    currentSession?.session_type,
    currentSessionTitle,
    getToken,
    sessionId,
    verifiedScope,
  ]);

  return { segments, loading, error };
}
